import { Sprite } from 'pixi.js';
import BallManager from './ballManager';

export enum UpgradeType {
    POWER_STRIKE = 'powerStrike',
    FEATHERWEIGHT = 'featherweight',
    BIG_BALL = 'bigBall',
    BOUNCY = 'bouncy',
    MAGNET_MASTER = 'magnetMaster',
    MULTIBALL = 'multiball'
}

export interface Upgrade {
    id: UpgradeType;
    name: string;
    description: string;
    icon: string; // emoji for now
    color: number;
    tier: number; // 1 (Common), 2 (Rare), 3 (Legendary)
    apply: (ballManager: BallManager) => void;
}

export default class UpgradeSystem {
    private ballManager: BallManager;
    private xp: number = 0;
    private level: number = 1;
    private xpToNextLevel: number = 100;
    private upgrades: Map<UpgradeType, number> = new Map(); // Upgrade Type -> Count

    constructor(ballManager: BallManager) {
        this.ballManager = ballManager;
    }

    public addXp(amount: number): boolean {
        this.xp += amount;
        let leveledUp = false;
        if (this.xp >= this.xpToNextLevel) {
            this.levelUp();
            leveledUp = true;
        }
        return leveledUp;
    }

    private levelUp(): void {
        this.level++;
        this.xp -= this.xpToNextLevel;
        this.xpToNextLevel = Math.floor(this.xpToNextLevel * 1.5);
        
        // Trigger UI in BallManager or UIManager
    }

    public getLevel(): number {
        return this.level;
    }

    public getXpProgress(): number {
        return this.xp / this.xpToNextLevel;
    }

    public getAvailableUpgrades(count: number = 3): Upgrade[] {
        const allUpgrades: Upgrade[] = [
            {
                id: UpgradeType.POWER_STRIKE,
                name: "Power Hitter",
                description: "+15% Strike Power",
                icon: "💪",
                color: 0xFF4500,
                tier: 1,
                apply: (bm) => {} // Logic handled via getters
            },
            {
                id: UpgradeType.FEATHERWEIGHT,
                name: "Featherweight",
                description: "Floatier physics (-10% Gravity effect)",
                icon: "🪶",
                color: 0x87CEEB,
                tier: 1,
                apply: (bm) => {}
            },
            {
                id: UpgradeType.BIG_BALL,
                name: "Big Ball",
                description: "+20% Ball Size",
                icon: "🎱",
                color: 0xFFA500,
                tier: 2,
                apply: (bm) => {
                    // Immediate effect if needed, otherwise handled in getters/update
                }
            },
            {
                id: UpgradeType.BOUNCY,
                name: "Super Bouncy",
                description: "+10% Bounciness",
                icon: "🏐",
                color: 0xFF69B4,
                tier: 1,
                apply: (bm) => {}
            },
            {
                id: UpgradeType.MAGNET_MASTER,
                name: "Magnet Master",
                description: "Attract coins from further away",
                icon: "🧲",
                color: 0x9370DB,
                tier: 2,
                apply: (bm) => {}
            }
        ];

        // Shuffle and pick
        return allUpgrades.sort(() => 0.5 - Math.random()).slice(0, count);
    }

    public selectUpgrade(upgrade: Upgrade): void {
        const currentCount = this.upgrades.get(upgrade.id) || 0;
        this.upgrades.set(upgrade.id, currentCount + 1);
        upgrade.apply(this.ballManager);
        
        // Apply immediate effects if necessary
        if (upgrade.id === UpgradeType.BIG_BALL) {
             // Trigger ball resize if possible or just store state
        }
    }

    // Getters for modifiers
    public getPowerMultiplier(): number {
        const count = this.upgrades.get(UpgradeType.POWER_STRIKE) || 0;
        return 1 + (count * 0.15);
    }

    public getGravityMultiplier(): number {
        const count = this.upgrades.get(UpgradeType.FEATHERWEIGHT) || 0;
        return Math.max(0.5, 1 - (count * 0.10)); // Cap at 50% gravity
    }

    public getSizeMultiplier(): number {
        const count = this.upgrades.get(UpgradeType.BIG_BALL) || 0;
        return 1 + (count * 0.20);
    }

    public getBouncinessMultiplier(): number {
        const count = this.upgrades.get(UpgradeType.BOUNCY) || 0;
        return 1 + (count * 0.10);
    }
    
    public getMagnetRadiusMultiplier(): number {
        const count = this.upgrades.get(UpgradeType.MAGNET_MASTER) || 0;
        return 1 + (count * 0.5);
    }
}

